/**
 * The rest in progress, kept outside the exercise screen: leaving it to look
 * at the session list (or iOS reloading the app in the background) used to
 * drop the countdown, and the rest went on unmeasured.
 */

export interface Rest {
  /** Wall-clock start, as `Date.now()`. */
  startedAt: number;
  seconds: number;
  /**
   * When it ended, skipped or run out: kept, not cleared, so the next set
   * can tell how long the rest before it really was.
   */
  endedAt?: number;
}

/**
 * Once the countdown is out, "C'est reparti" shows this long, and the rest
 * is taken as over: the athlete is back at it within these seconds. No
 * overtime is counted any more — the timer was never stopped, so it only
 * ever measured the next set as well.
 */
export const BACK_TO_IT_SECONDS = 10;

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** Past this long after its end, a rest is no longer the one being taken. */
const STALE_AFTER_SECONDS = 15 * 60;

const key = (sessionId: number) => `rest:${sessionId}`;

/**
 * Whoever shows the rest — the movement's screen, the bar over the other
 * screens — hears of every change, so a +30 s on one is the same rest on the
 * other.
 */
const listeners = new Set<() => void>();

export function subscribeRest(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Where the rest lives when storage refuses it: this page's memory, for as long as it is open. */
const memory = new Map<string, string>();

/** The stored rest as written, whatever its age; null when unreadable. */
export function decodeRest(raw: string | null): Rest | null {
  if (!raw) return null;
  try {
    const { startedAt, seconds, endedAt } = JSON.parse(raw) as Partial<Rest>;
    if (typeof startedAt !== "number" || typeof seconds !== "number") return null;
    if (!Number.isFinite(startedAt) || !Number.isFinite(seconds) || seconds <= 0) return null;
    return typeof endedAt === "number" && Number.isFinite(endedAt)
      ? { startedAt, seconds, endedAt }
      : { startedAt, seconds };
  } catch {
    return null;
  }
}

/** The stored rest if it still stands: not ended, and not over by more than the moment to get back to it. */
export function parseRest(raw: string | null, now: number): Rest | null {
  const rest = decodeRest(raw);
  if (!rest || rest.endedAt !== undefined) return null;
  if (now - rest.startedAt > (rest.seconds + BACK_TO_IT_SECONDS) * 1000) return null;
  return rest;
}

/**
 * The rest really taken before a set validated `now`, in seconds; null when
 * there was none to speak of. Ended (skipped, or run out and closed): until
 * then. Still running: the set was begun before its end, so its own length
 * (`setSeconds`, estimated) is not rest.
 */
export function restTaken(rest: Rest | null, now: number, setSeconds: number): number | null {
  if (!rest || now - rest.startedAt > STALE_AFTER_SECONDS * 1000) return null;
  const end = rest.startedAt + rest.seconds * 1000;
  const until = rest.endedAt ?? (now >= end ? end : now - setSeconds * 1000);
  return Math.max(0, Math.round((Math.min(until, end, now) - rest.startedAt) / 1000));
}

// Storage throws in private browsing and when it is full. The countdown then
// lives in the screen's own state, as it did before: nothing is lost that
// was ever promised, so this is not worth an error on the phone mid-set.
function storage(): Store | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The stored text as is: what a subscriber compares to know whether anything changed. */
export function restSnapshot(sessionId: number, store: Store | null = storage()): string | null {
  try {
    return store?.getItem(key(sessionId)) ?? memory.get(key(sessionId)) ?? null;
  } catch {
    return memory.get(key(sessionId)) ?? null;
  }
}

/** The stored text of a rest that still stands, else null: stable while nothing changes. */
export function freshRestSnapshot(sessionId: number, now = Date.now()): string | null {
  const raw = restSnapshot(sessionId);
  return parseRest(raw, now) ? raw : null;
}

export function loadRest(sessionId: number, now = Date.now(), store: Store | null = storage()): Rest | null {
  return parseRest(restSnapshot(sessionId, store), now);
}

/** Lengthened or shortened by `delta` seconds, never under ten. */
export function adjustRest(rest: Rest, delta: number): Rest {
  return { startedAt: rest.startedAt, seconds: Math.max(10, rest.seconds + delta) };
}

/** Ended now (skipped), or at its own end when it ran out: never later. */
export function endRest(rest: Rest, now: number): Rest {
  return { ...rest, endedAt: Math.min(now, rest.startedAt + rest.seconds * 1000) };
}

/** Seconds left, below zero once over. */
export function restRemaining(rest: Rest, now = Date.now()): number {
  return Math.ceil(rest.seconds - (now - rest.startedAt) / 1000);
}

/** Null clears it: the rest was skipped, or the next set has started. */
export function saveRest(sessionId: number, rest: Rest | null, store: Store | null = storage()): void {
  if (rest === null) memory.delete(key(sessionId));
  else memory.set(key(sessionId), JSON.stringify(rest));
  try {
    if (rest === null) store?.removeItem(key(sessionId));
    else store?.setItem(key(sessionId), JSON.stringify(rest));
  } catch {
    /* see storage() */
  }
  for (const listener of listeners) listener();
}
